import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';
import { getAvatarStyle } from '../utils/avatar';
import { Card, CardHeader } from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge, { MemoryBadge, ScoreBadge } from '../components/UI/Badge';
import { Input, Select, Textarea } from '../components/UI/Input';
import Breadcrumb from '../components/Breadcrumb';
import { normalizeBirthDate, contactAge, formatBirthDateFr } from '../utils/dateUtils';
import { qualityScore, qualityBreakdown } from '../utils/contactQuality';
import './ContactDetail.css';

const GENDER_LABELS = {
  monsieur: { label: 'M.',   color: '#2563EB', bg: '#EFF6FF' },
  madame:   { label: 'Mme',  color: '#DB2777', bg: '#FDF2F8' },
  autre:    { label: 'X',    color: '#7C3AED', bg: '#F5F3FF' },
};

// Dates de naissance : brique unique partagée (répare les années 2 chiffres
// mal stockées, ex. « 0074 » → « 1974 »). Voir src/utils/dateUtils.js.
const normalizeDateStr = normalizeBirthDate;
const getAge = contactAge;

export default function ContactDetail() {
  const { id } = useParams();
  const { contacts, campaigns, updateContact, deleteContact, addContact, addNotification } = useApp();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const contactFromCtx = isNew ? null : contacts.find(c => String(c.id) === String(id));

  // Default for new contacts — RGPD activé par défaut
  // Conformité nLPD : le consentement n'est JAMAIS présumé — null = à demander.
  // L'utilisateur le coche explicitement s'il a recueilli l'accord.
  const NEW_DEFAULT = { status: 'active', tone: 'auto', category: 'Régulier', gdpr_consent: null };

  const [contact, setContact] = useState(contactFromCtx);
  const [loadingContact, setLoadingContact] = useState(!isNew && !contactFromCtx);

  const initialRef = useRef(contactFromCtx ? { ...contactFromCtx, birth_date: normalizeDateStr(contactFromCtx.birth_date) } : NEW_DEFAULT);
  const loadedRef  = useRef(!!contactFromCtx);

  const [form, setForm] = useState(initialRef.current);
  const [dirty, setDirty] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingBirthDate, setEditingBirthDate] = useState(false);

  // ── Fallback: charge le contact depuis l'API s'il n'est pas dans le contexte ─
  useEffect(() => {
    if (isNew) return;
    if (contactFromCtx) {
      setContact(contactFromCtx);
      if (!loadedRef.current) {
        loadedRef.current = true;
        const normalized = { ...contactFromCtx, birth_date: normalizeDateStr(contactFromCtx.birth_date) };
        initialRef.current = normalized;
        setForm(normalized);
        setDirty(false);
      }
      return;
    }
    // Pas dans le contexte → charge depuis l'API
    setLoadingContact(true);
    api.getContact(id).then(c => {
      if (c) {
        setContact(c);
        const normalized = { ...c, birth_date: normalizeDateStr(c.birth_date) };
        initialRef.current = normalized;
        loadedRef.current = true;
        setForm(normalized);
        setDirty(false);
      }
    }).catch(() => {}).finally(() => setLoadingContact(false));
  }, [id, isNew, contactFromCtx]);

  if (loadingContact) return <div style={{ padding: 40, color: 'var(--muted)' }}>Chargement…</div>;
  if (!isNew && !contact) return (
    <div style={{ padding: 40, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
      <div>Contact introuvable.</div>
      <button onClick={() => navigate('/contacts/new')} style={{ background: 'var(--violet)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>+ Nouveau contact</button>
      <button onClick={() => navigate('/contacts')} style={{ background: 'transparent', color: 'var(--muted)', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>← Retour aux contacts</button>
    </div>
  );

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await addContact(form);
        addNotification('Contact ajouté avec succès !');
        navigate('/contacts');
      } else {
        await updateContact(contact.id, form);
        initialRef.current = { ...form };
        setDirty(false);
        addNotification('Contact mis à jour.');
      }
    } catch (err) {
      addNotification(`Erreur : ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({ ...initialRef.current });
    setDirty(false);
  };

  const handleDelete = () => {
    deleteContact(contact.id);
    addNotification('Contact supprimé.');
    navigate('/contacts');
  };

  const age = getAge(form.birth_date);
  const isRoundAge = age !== null && age % 10 === 0;

  const avatarStyle = getAvatarStyle(form.first_name);

  return (
    <div className="contact-detail">
      <Breadcrumb items={[
        { label: 'Contacts', icon: '👥', onClick: () => navigate('/contacts') },
        { label: isNew ? 'Nouveau contact' : [form.first_name, form.last_name].filter(Boolean).join(' ') || 'Fiche contact' },
      ]} />
      {/* ── Hero strip (contact existant) ou topbar simple (nouveau) ── */}
      {!isNew ? (
        <div className="cd-hero" style={{ background: avatarStyle.background }}>
          <div className="cd-hero-nebula" />
          <div className="cd-hero-inner">
            <div className="cd-hero-left">
              <Link to="/contacts" className="cd-hero-back">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                Contacts
              </Link>
              <div className={`db2-avatar-wrap${isRoundAge ? ' db2-avatar-wrap--round' : ''}`}>
                <div className="cd-hero-avatar" style={avatarStyle}>
                  {(form.first_name || '?').charAt(0)}{(form.last_name || '').charAt(0)}
                </div>
                {isRoundAge && <span className="db2-avatar-age">{age}</span>}
              </div>
              <div className="cd-hero-identity">
                <div className="cd-hero-name">
                  {GENDER_LABELS[form.gender] && (
                    <span className="cd-hero-gender" style={{
                      background: GENDER_LABELS[form.gender].bg,
                      color: GENDER_LABELS[form.gender].color,
                    }}>{GENDER_LABELS[form.gender].label}</span>
                  )}
                  {form.first_name || '—'} {form.last_name || ''}
                </div>
                <div className="cd-hero-meta">
                  {age !== null && <span>🎂 {age} ans</span>}
                  {form.city && <span>📍 {form.city}</span>}
                  {form.category && <span className="cd-hero-cat">{form.category}</span>}
                  <span className={`cd-hero-status ${form.status === 'active' ? 'cd-hero-status--active' : 'cd-hero-status--inactive'}`}>
                    {form.status === 'active' ? '● Actif' : '○ Inactif'}
                  </span>
                </div>
              </div>
            </div>
            <div className="cd-hero-right">
              {[
                { label: 'Qualité',     val: form.quality_score     || 0 },
                { label: 'Engagement',  val: form.engagement_score  || 0 },
                { label: 'Opportunité', val: form.opportunity_score || 0 },
              ].map(s => (
                <div key={s.label} className="cd-hero-score">
                  <span className="cd-hero-score-val">{s.val}</span>
                  <span className="cd-hero-score-lbl">{s.label}</span>
                </div>
              ))}
              <button className="cd-hero-close" onClick={() => navigate('/contacts')}>✕</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="contact-detail-topbar">
          <div className="breadcrumb">
            <Link to="/contacts" className="breadcrumb-link">Contacts</Link>
            <span> / </span>
            <span>Nouveau contact</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/contacts')}>✕ Fermer</Button>
        </div>
      )}

      {/* Sticky save bar — appears when dirty */}
      {dirty && (
        <div className="contact-save-bar">
          <span className="contact-save-bar-msg">
            {isNew ? 'Nouveau contact — remplissez les champs ci-dessous' : '● Modifications non enregistrées'}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isNew && <Button variant="ghost" size="sm" onClick={handleCancel}>Annuler</Button>}
            <Button size="sm" onClick={handleSave} loading={saving}>Enregistrer</Button>
          </div>
        </div>
      )}

      <div className="contact-detail-grid">
        {/* Left: profile card */}
        <div className="contact-profile-col">
          <Card>
            <div className="contact-profile-header">
              {/* Gradient banner — uses same color as avatar */}
              <div className="contact-profile-banner"
                style={{ background: getAvatarStyle(form.first_name).background || 'linear-gradient(135deg,#5A35FF,#7C3AED)' }} />
              <div className={`contact-big-avatar${isRoundAge ? ' cd-avatar-round' : ''}`} style={getAvatarStyle(form.first_name)}>
                {form.first_name ? form.first_name.charAt(0) : '?'}
                {form.last_name ? form.last_name.charAt(0) : ''}
                {isRoundAge && <span className="db2-avatar-age">{age}</span>}
              </div>
              <h2 className="contact-big-name">
                {GENDER_LABELS[form.gender] && (
                  <span style={{ fontSize: 13, fontWeight: 700, padding: '2px 8px', borderRadius: 20, marginRight: 6,
                    background: GENDER_LABELS[form.gender].bg, color: GENDER_LABELS[form.gender].color }}>
                    {GENDER_LABELS[form.gender].label}
                  </span>
                )}
                {form.first_name || '—'} {form.last_name || ''}
              </h2>
              {form.city && <p className="contact-big-city">📍 {form.city}{form.canton ? `, ${form.canton}` : ''}</p>}
              {age !== null && (
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: '2px 0 4px', fontWeight: 600 }}>
                  🎂 {age} ans
                  {(() => {
                    const bd = normalizeDateStr(form.birth_date);
                    if (!bd) return null;
                    const [, m, d] = bd.split('-').map(Number);
                    const today = new Date();
                    let bday = new Date(today.getFullYear(), m - 1, d);
                    if (bday < today) bday.setFullYear(today.getFullYear() + 1);
                    const diff = Math.round((bday - today) / 86400000);
                    if (diff === 0)  return <span style={{ color: '#EF4444', marginLeft: 6 }}>🎉 Anniversaire aujourd'hui !</span>;
                    if (diff <= 7)  return <span style={{ color: '#F59E0B', marginLeft: 6 }}>🕐 dans {diff} jour{diff > 1 ? 's' : ''}</span>;
                    if (diff <= 30) return <span style={{ color: '#6B7280', marginLeft: 6 }}>· dans {diff} jours</span>;
                    return null;
                  })()}
                </p>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                {form.category && <Badge variant="violet">{form.category}</Badge>}
                <Badge variant={form.status === 'active' ? 'green' : 'gray'} dot>
                  {form.status === 'active' ? 'Actif' : 'Inactif'}
                </Badge>
                {form.relationship_memory && <MemoryBadge />}
                {form.gdpr_consent && (
                  <Badge variant="green">✅ RGPD</Badge>
                )}
              </div>

              {/* ── Complétude fiche ─────────────────────────────────── */}
              {(() => {
                const score = qualityScore(form);
                const color  = score >= 80 ? '#10B981' : score >= 50 ? '#F59E0B' : '#EF4444';
                const bg     = score >= 80 ? '#ECFDF5' : score >= 50 ? '#FFFBEB' : '#FEF2F2';
                const label  = score >= 80 ? 'Fiche complète' : score >= 50 ? 'Fiche partielle' : 'Fiche incomplète';
                return (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color, background: bg,
                        padding: '2px 10px', borderRadius: 20, border: `1px solid ${color}33` }}>
                        {label} — {score}%
                      </span>
                    </div>
                    <div style={{ width: 140, height: 5, background: '#E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                      <div style={{ width: `${score}%`, height: '100%', background: color,
                        borderRadius: 10, transition: 'width .4s ease' }} />
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="contact-scores">
              <div className="score-item">
                <div className="score-label">Qualité</div>
                <ScoreBadge score={form.quality_score || 0} />
              </div>
              <div className="score-item">
                <div className="score-label">Engagement</div>
                <ScoreBadge score={form.engagement_score || 0} />
              </div>
              <div className="score-item">
                <div className="score-label">Opportunité</div>
                <ScoreBadge score={form.opportunity_score || 0} />
              </div>
              <div className="score-item">
                <div className="score-label">Fidélité</div>
                <ScoreBadge score={form.loyalty_score || 0} />
              </div>
            </div>

            {/* Appee insight opportunité */}
            <OpportunityInsight contact={form} age={age} />

            {/* Quality score breakdown */}
            <div style={{ margin: '8px 0', padding: '10px 14px', background: 'var(--bg)', borderRadius: 12, fontSize: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--muted)' }}>Score qualité — détail</div>
              {qualityBreakdown(form).map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 10 }}>{item.done ? '✅' : '⬜'}</span>
                  <span style={{ flex: 1, color: item.done ? 'var(--text)' : 'var(--muted)' }}>{item.label}</span>
                  <span style={{ fontWeight: 700, color: item.done ? 'var(--success)' : 'var(--muted-2)', fontSize: 11 }}>
                    {item.done ? `+${item.pts}` : `+${item.pts} manquant`}
                  </span>
                </div>
              ))}
            </div>

            {/* Quick info */}
            <div className="contact-info-list">
              {form.email && <InfoRow icon="📧" label="E-mail" value={<a href={`mailto:${form.email}`}>{form.email}</a>} />}
              {form.phone && <InfoRow icon="📱" label="Téléphone" value={form.phone} />}
              {form.birth_date && <InfoRow icon="🎂" label="Anniversaire" value={`${formatBirthDateFr(form.birth_date) || form.birth_date}${age !== null ? ` (${age} ans)` : ''}`} />}
              {form.address && <InfoRow icon="🏠" label="Adresse" value={`${form.address}, ${form.postal_code || ''} ${form.city || ''}`} />}
              <InfoRow icon="💬" label="Ton" value={{ auto: 'Automatique', tutoiement: 'Tutoiement', vouvoiement: 'Vouvoiement' }[form.tone] || 'Auto'} />
            </div>

            {/* Relationship memory */}
            {form.relationship_memory && (
              <div className="note-box" style={{ marginBottom: 16 }}>
                <span className="note-box-icon">💡</span>
                <span className="note-box-text">{form.relationship_memory}</span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <Button variant="ghost" size="sm" onClick={() => navigate('/contacts')} style={{ flex: 1 }}>
                ← Retour
              </Button>
              {!isNew && (
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>🗑</Button>
              )}
            </div>

            {confirmDelete && (
              <div className="delete-confirm">
                <p>Supprimer ce contact définitivement ?</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="sm" variant="danger" onClick={handleDelete}>Supprimer</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(false)}>Annuler</Button>
                </div>
              </div>
            )}
          </Card>

          {/* 🛡 Preuves de consentement (Lot 1) — jamais un simple « oui » */}
          {!isNew && (form.consent_proofs?.length > 0 || form.gdpr_consent != null || form.sms_consent != null) && (
            <Card>
              <CardHeader title="🛡 Consentements" subtitle="Ce que ce contact a accepté, quand, et avec quel texte" />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: (form.consent_proofs?.length ? 12 : 0) }}>
                <span style={{ fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '4px 12px', background: form.gdpr_consent ? '#ECFDF5' : '#F3F4F6', color: form.gdpr_consent ? '#065F46' : '#6B7280' }}>
                  📧 E-mail : {form.gdpr_consent ? `accepté${form.gdpr_consent_at ? ' le ' + new Date(form.gdpr_consent_at).toLocaleDateString('fr-CH') : ''}` : form.gdpr_consent === false ? 'refusé' : 'non donné'}
                </span>
                <span style={{ fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '4px 12px', background: form.sms_consent ? '#ECFDF5' : '#F3F4F6', color: form.sms_consent ? '#065F46' : '#6B7280' }}>
                  📱 SMS : {form.sms_consent ? 'accepté' : form.sms_consent === false ? 'refusé' : 'non donné'}
                </span>
              </div>
              {(form.consent_proofs || []).map((pr, i) => (
                <div key={i} style={{ background: 'var(--violet-soft, #F5F3FF)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 12px', marginBottom: 8, fontSize: 12, lineHeight: 1.55 }}>
                  <b style={{ color: 'var(--ink, #1A1535)' }}>🧲 {pr.consents?.campagne || 'Collecte'}</b>
                  <span style={{ color: 'var(--muted)' }}> · {pr.at ? new Date(pr.at).toLocaleString('fr-CH') : ''}</span>
                  {['participation', 'email', 'sms'].map(k => {
                    const c = pr.consents?.[k];
                    if (!c || (!c.ok && !c.text)) return null;
                    return (
                      <div key={k} style={{ marginTop: 5, color: '#374151' }}>
                        {c.ok ? '✅' : '⬜'} <b>{k === 'participation' ? 'Participation' : k === 'email' ? 'E-mail' : 'SMS'}</b>
                        {c.v ? <span style={{ color: 'var(--muted)' }}> (v. {c.v})</span> : null}
                        {c.ok && c.text ? <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', fontStyle: 'italic' }}>« {c.text} »</span> : null}
                      </div>
                    );
                  })}
                </div>
              ))}
            </Card>
          )}

          {/* Email stats */}
          {!isNew && (
            <Card>
              <CardHeader title="📧 Historique e-mail" />
              {(() => {
                const sent     = form.emails_sent    || 0;
                const opened   = form.emails_opened  || 0;
                const feedback = form.feedback_count || 0;
                const rate     = sent > 0 ? Math.round((opened / sent) * 100) : null;
                const rateColor = rate === null ? '#9CA3AF' : rate >= 35 ? '#10B981' : rate >= 15 ? '#F59E0B' : '#EF4444';
                return (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                      {[
                        { icon: '📤', label: 'Envoyés',   value: sent,     color: '#6B7280' },
                        { icon: '👁',  label: 'Ouverts',   value: opened,   color: '#3B82F6' },
                        { icon: '💬', label: 'Feedbacks', value: feedback,  color: '#EC4899' },
                      ].map(s => (
                        <div key={s.label} style={{ textAlign: 'center', padding: '10px 8px', background: 'var(--bg)', borderRadius: 10 }}>
                          <div style={{ fontSize: 18, marginBottom: 2 }}>{s.icon}</div>
                          <div style={{ fontSize: 19, fontWeight: 800, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                    {sent > 0 && (
                      <div style={{ padding: '12px 14px', background: 'var(--bg)', borderRadius: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                          <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Taux d'ouverture</span>
                          <span style={{ color: rateColor, fontWeight: 800 }}>{rate}%</span>
                        </div>
                        <div style={{ position: 'relative', height: 8, background: '#E5E7EB', borderRadius: 4 }}>
                          <div title="Moyenne secteur 35%" style={{ position: 'absolute', left: '35%', top: -2, bottom: -2, width: 2, background: '#94A3B8', borderRadius: 1, zIndex: 2 }} />
                          <div style={{ width: `${Math.min(rate, 100)}%`, height: '100%', background: rateColor, borderRadius: 4, transition: 'width .4s' }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, marginTop: 4 }}>
                          <span style={{ color: rateColor, fontWeight: 600 }}>
                            {rate >= 35 ? '✅ Au-dessus de la moyenne' : '⚠️ Sous la moyenne'}
                          </span>
                          <span style={{ color: '#94A3B8' }}>Moy. 35%</span>
                        </div>
                      </div>
                    )}
                    {sent === 0 && (
                      <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 12, color: 'var(--muted)' }}>
                        Aucun e-mail envoyé à ce contact pour l'instant
                      </div>
                    )}
                  </>
                );
              })()}
            </Card>
          )}
        </div>

        {/* Right: form + campaigns */}
        <div className="contact-form-col">
          <Card>
            <CardHeader title={isNew ? 'Nouveau contact' : 'Informations'} />
            <div className="contact-form">
              <div className="form-row">
                <Input label="Prénom" value={form.first_name || ''} onChange={e => set('first_name', e.target.value)} />
                <Input label="Nom" value={form.last_name || ''} onChange={e => set('last_name', e.target.value)} />
              </div>
              <Input label="E-mail" type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} />
              <Input label="Téléphone" type="tel" value={form.phone || ''} onChange={e => set('phone', e.target.value)} />

              {/* Date de naissance — affichage fixe + bouton Modifier */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>Date de naissance</div>
                {!editingBirthDate && form.birth_date ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--bg)', minHeight: 42 }}>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                      {formatBirthDateFr(form.birth_date) || form.birth_date}
                    </span>
                    {age !== null && (
                      <span style={{
                        background: age % 10 === 0 ? '#FEF3C7' : '#EDE9FF',
                        color: age % 10 === 0 ? '#D97706' : '#5A35FF',
                        fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 20, flexShrink: 0,
                      }}>
                        {age} ans{age % 10 === 0 ? ' 🎯' : ''}
                      </span>
                    )}
                    <button
                      onClick={() => setEditingBirthDate(true)}
                      style={{ background: 'none', border: '1px solid var(--line)', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--muted)', flexShrink: 0 }}
                    >✏️ Modifier</button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="date"
                      value={form.birth_date || ''}
                      onChange={e => { set('birth_date', e.target.value); }}
                      onBlur={() => { if (form.birth_date) setEditingBirthDate(false); }}
                      autoFocus={editingBirthDate}
                      style={{ width: '100%', border: '1px solid #5A35FF', borderRadius: 8, padding: '10px 14px', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: '#fff' }}
                    />
                    {age !== null && (
                      <span style={{
                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                        background: age % 10 === 0 ? '#FEF3C7' : '#EDE9FF',
                        color: age % 10 === 0 ? '#D97706' : '#5A35FF',
                        fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 20, pointerEvents: 'none',
                      }}>
                        {age} ans{age % 10 === 0 ? ' 🎯' : ''}
                      </span>
                    )}
                    {form.birth_date && (
                      <button
                        onClick={() => setEditingBirthDate(false)}
                        style={{ marginTop: 4, background: 'none', border: 'none', fontSize: 12, color: 'var(--muted)', cursor: 'pointer' }}
                      >✓ Confirmer</button>
                    )}
                  </div>
                )}
              </div>

              <div className="form-section-title">Adresse</div>
              <Input label="Rue et numéro" value={form.address || ''} onChange={e => set('address', e.target.value)} />
              <div className="form-row">
                <Input label="NPA" value={form.postal_code || ''} onChange={e => set('postal_code', e.target.value)} />
                <Input label="Ville" value={form.city || ''} onChange={e => set('city', e.target.value)} />
              </div>
              <div className="form-row">
                <Input label="Région" value={form.region || ''} onChange={e => set('region', e.target.value)} placeholder="Ex: Valais central, Riviera…" />
                <Input label="Canton" value={form.canton || ''} onChange={e => set('canton', e.target.value)} />
              </div>
              <Input label="Pays" value={form.country || ''} onChange={e => set('country', e.target.value)} placeholder="Suisse" />

              <div className="form-section-title">Paramètres</div>
              <div className="form-row">
                <Select label="Civilité" value={form.gender || ''} onChange={e => set('gender', e.target.value)}>
                  <option value="">— Non renseigné —</option>
                  <option value="monsieur">Monsieur</option>
                  <option value="madame">Madame</option>
                  <option value="autre">Autre / Non-binaire</option>
                </Select>
              </div>
              <div className="form-row">
                <Select label="Catégorie" value={form.category || ''} onChange={e => set('category', e.target.value)}>
                  {['VIP','Fidèle','Régulier','Occasionnel','Inactif','Nouveau'].map(c => <option key={c}>{c}</option>)}
                </Select>
                <Select label="Ton relationnel" value={form.tone || 'auto'} onChange={e => set('tone', e.target.value)}>
                  <option value="auto">Automatique</option>
                  <option value="tutoiement">Tutoiement</option>
                  <option value="vouvoiement">Vouvoiement</option>
                </Select>
                <Select label="Statut" value={form.status || 'active'} onChange={e => set('status', e.target.value)}>
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </Select>
              </div>

              <div className="form-section-title">Tags</div>
              <TagsInput
                value={form.tags || []}
                onChange={tags => set('tags', tags)}
              />

              <Textarea
                label="💡 Mémoire relationnelle"
                value={form.relationship_memory || ''}
                onChange={e => set('relationship_memory', e.target.value)}
                hint="Préférences, informations importantes, contexte relationnel…"
                rows={3}
              />

              <div className="form-section-title">Consentements</div>

              {/* RGPD e-mail */}
              <ConsentToggle
                value={form.gdpr_consent}
                onChange={v => set('gdpr_consent', v)}
                label={form.gdpr_consent ? '✅ Consentement e-mail (RGPD)' : '⬜ Consentement e-mail (RGPD)'}
                hint={form.gdpr_consent_at ? `Donné le ${new Date(form.gdpr_consent_at).toLocaleDateString('fr-CH')}` : "Autorisation d'envoyer des e-mails marketing"}
                color="#10B981"
              />

              {/* SMS consent */}
              <ConsentToggle
                value={form.sms_consent}
                onChange={v => set('sms_consent', v)}
                label={form.sms_consent ? '✅ Consentement SMS' : '⬜ Consentement SMS'}
                hint={form.sms_opted_out ? '🚫 Contact désinscrit des SMS' : "Autorisation d'envoyer des SMS promotionnels"}
                color="#3B82F6"
              />

              {/* Bottom action bar */}
              <div className="contact-form-actions">
                <Button variant="ghost" onClick={() => navigate('/contacts')}>✕ Fermer</Button>
                {dirty && (
                  <>
                    {!isNew && <Button variant="ghost" onClick={handleCancel}>Annuler</Button>}
                    <Button onClick={handleSave}>Enregistrer</Button>
                  </>
                )}
              </div>
            </div>
          </Card>

          {/* Campaigns timeline */}
          {!isNew && (
            <ContactCampaigns contactId={contact.id} contactEmail={contact.email} campaigns={campaigns} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Campaigns Timeline ────────────────────────────────────────────────────────
const CAMPAIGN_STATUS_META = {
  sent:      { label: 'Envoyée',    color: '#10B981', bg: '#ECFDF5' },
  sending:   { label: 'En cours',   color: '#3B82F6', bg: '#EFF6FF' },
  scheduled: { label: 'Planifiée',  color: '#F59E0B', bg: '#FFFBEB' },
  draft:     { label: 'Brouillon',  color: '#6B7280', bg: '#F3F4F6' },
  cancelled: { label: 'Annulée',    color: '#EF4444', bg: '#FEF2F2' },
};

const CAMPAIGN_TYPE_ICON = {
  birthday: '🎂', reactivation: '💤', promo: '🎁',
  loyalty: '💙', local: '📍', custom: '✉️',
};

function ContactCampaigns({ contactId, contactEmail, campaigns }) {
  const lsKey = `appee_cnotes_${contactId}`;
  const [notes, setNotes] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem(lsKey) || '{}'); } catch { return {}; }
  });
  const [drafts, setDrafts] = React.useState({});
  const [expanded, setExpanded] = React.useState({});

  // Charger les notes depuis la BDD au montage
  React.useEffect(() => {
    if (!contactId) return;
    api.getContactCampaignNotes(contactId)
      .then(data => {
        if (data && Object.keys(data).length > 0) {
          setNotes(data);
          localStorage.setItem(lsKey, JSON.stringify(data)); // sync local
        }
      })
      .catch(() => {}); // fallback: garder le localStorage
  }, [contactId, lsKey]);

  const persistNotes = useCallback((updated) => {
    setNotes(updated);
    localStorage.setItem(lsKey, JSON.stringify(updated));
    api.saveContactCampaignNotes(contactId, updated).catch(() => {});
  }, [contactId, lsKey]);

  const saveNote = useCallback((campaignId) => {
    const text = (drafts[campaignId] || '').trim();
    if (!text) return;
    const timestamp = new Date().toISOString();
    const updated = {
      ...notes,
      [campaignId]: [...(notes[campaignId] || []), { text, timestamp }],
    };
    persistNotes(updated);
    setDrafts(d => ({ ...d, [campaignId]: '' }));
  }, [drafts, notes, persistNotes]);

  const deleteNote = useCallback((campaignId, idx) => {
    const updated = {
      ...notes,
      [campaignId]: (notes[campaignId] || []).filter((_, i) => i !== idx),
    };
    persistNotes(updated);
  }, [notes, persistNotes]);

  // Montrer toutes campagnes (envoyées/en cours/planifiées) triées par date desc
  const relevant = [...campaigns]
    .filter(c => ['sent', 'sending', 'scheduled'].includes(c.status))
    .sort((a, b) => new Date(b.sent_at || b.scheduled_at || b.created_at) - new Date(a.sent_at || a.scheduled_at || a.created_at));

  if (relevant.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="📋 Campagnes & historique"
        subtitle="Campagnes envoyées ou planifiées — ajoutez des notes de suivi"
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {relevant.map(camp => {
          const meta    = CAMPAIGN_STATUS_META[camp.status] || CAMPAIGN_STATUS_META.draft;
          const icon    = CAMPAIGN_TYPE_ICON[camp.type] || '✉️';
          const date    = camp.sent_at || camp.scheduled_at || camp.created_at;
          const dateStr = date ? new Date(date).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
          const campNotes = notes[camp.id] || [];
          const isOpen  = expanded[camp.id];

          return (
            <div key={camp.id} style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden' }}>
              {/* Campaign header row */}
              <div
                onClick={() => setExpanded(e => ({ ...e, [camp.id]: !e[camp.id] }))}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', background: isOpen ? '#FAFAFA' : '#fff' }}
              >
                <span style={{ fontSize: 18 }}>{icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{camp.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{dateStr} · {camp.sent_count ?? camp.target_count ?? '—'} destinataires</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: meta.bg, color: meta.color, flexShrink: 0 }}>{meta.label}</span>
                <span style={{ color: 'var(--muted)', fontSize: 12, marginLeft: 4 }}>{isOpen ? '▲' : '▼'}</span>
              </div>

              {/* Expanded: notes timeline + input */}
              {isOpen && (
                <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--line)' }}>
                  {/* Existing notes */}
                  {campNotes.length > 0 && (
                    <div style={{ paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {campNotes.map((n, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <div style={{ width: 2, background: '#5A35FF', borderRadius: 2, alignSelf: 'stretch', flexShrink: 0, minHeight: 20 }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>
                              {new Date(n.timestamp).toLocaleDateString('fr-CH', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{n.text}</div>
                          </div>
                          <button
                            onClick={() => deleteNote(camp.id, i)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 11, padding: '2px 4px', borderRadius: 4, flexShrink: 0 }}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add note */}
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <input
                      value={drafts[camp.id] || ''}
                      onChange={e => setDrafts(d => ({ ...d, [camp.id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveNote(camp.id); } }}
                      placeholder="Ajouter une note de suivi…"
                      style={{ flex: 1, border: '1px solid var(--line)', borderRadius: 8, padding: '7px 11px', fontSize: 13, outline: 'none', background: 'var(--bg)' }}
                    />
                    <button
                      onClick={() => saveNote(camp.id)}
                      style={{ background: '#5A35FF', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
                    >+ Note</button>
                  </div>

                  {/* Campaign stats if available */}
                  {camp.status === 'sent' && (
                    <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                      {[
                        { label: 'Envoyés',  val: camp.sent_count ?? '—' },
                        { label: 'Ouverts',  val: camp.opened_count ?? '—' },
                        { label: 'Tx ouv.',  val: camp.open_rate != null ? `${camp.open_rate}%` : '—' },
                      ].map(s => (
                        <div key={s.label} style={{ flex: 1, textAlign: 'center', background: 'var(--bg)', borderRadius: 8, padding: '8px 4px' }}>
                          <div style={{ fontSize: 16, fontWeight: 800 }}>{s.val}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Opportunity Insight (§34.4 CDC) ──────────────────────────────────────────
function OpportunityInsight({ contact, age }) {
  const score = contact.opportunity_score || 0;
  if (score < 30 || !contact.birth_date) return null;

  const reasons = [];
  const bdNorm = normalizeDateStr(contact.birth_date);
  if (bdNorm) {
    const today = new Date();
    const [, m, d] = bdNorm.split('-').map(Number);
    let bday = new Date(today.getFullYear(), m - 1, d);
    if (bday < today) bday.setFullYear(today.getFullYear() + 1);
    const diffDays = Math.round((bday - today) / (1000 * 3600 * 24));
    if (diffDays <= 7)  reasons.push(`🎂 Anniversaire dans ${diffDays === 0 ? "aujourd'hui" : diffDays + ' jour' + (diffDays > 1 ? 's' : '')}`);
    else if (diffDays <= 30) reasons.push(`🎂 Anniversaire dans ${diffDays} jours`);
    if (age && age % 10 === 0) reasons.push(`🎯 Âge rond — ${age} ans, occasion idéale`);
  }
  if (contact.category === 'VIP') reasons.push('⭐ Client VIP — priorité haute');
  if (!contact.email) reasons.push('📧 Pas encore d\'e-mail — collecter pour agir');

  const color  = score >= 70 ? '#5A35FF' : score >= 50 ? '#F59E0B' : '#6B7280';
  const bg     = score >= 70 ? '#EDE9FF' : score >= 50 ? '#FFFBEB' : '#F9FAFB';
  const border = score >= 70 ? '#A78BFA' : score >= 50 ? '#FCD34D' : '#E5E7EB';
  const label  = score >= 70 ? 'Opportunité forte' : score >= 50 ? 'Opportunité moyenne' : 'À surveiller';

  if (reasons.length === 0) return null;

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color, marginBottom: 6 }}>🎯 {label} — score {score}/100</div>
      {reasons.map((r, i) => (
        <div key={i} style={{ fontSize: 12, color: '#374151', marginBottom: 2 }}>{r}</div>
      ))}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="info-row">
      <span className="info-icon">{icon}</span>
      <div>
        <div className="info-label">{label}</div>
        <div className="info-value">{value}</div>
      </div>
    </div>
  );
}

function TagsInput({ value, onChange }) {
  const [input, setInput] = React.useState('');
  const tags = Array.isArray(value) ? value : [];

  const add = () => {
    const tag = input.trim();
    if (!tag || tags.includes(tag)) { setInput(''); return; }
    onChange([...tags, tag]);
    setInput('');
  };

  const remove = (t) => onChange(tags.filter(x => x !== t));

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: 6 }}>🏷 Tags</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {tags.map(t => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EDE9FF', color: '#5A35FF', fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>
            {t}
            <button onClick={() => remove(t)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5A35FF', fontSize: 12, padding: 0, lineHeight: 1 }}>✕</button>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Ajouter un tag…"
          style={{ flex: 1, border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px', fontSize: 13, outline: 'none' }}
        />
        <button onClick={add} style={{ background: '#5A35FF', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>+</button>
      </div>
    </div>
  );
}

// ── ConsentToggle ─────────────────────────────────────────────────────────────
function ConsentToggle({ value, onChange, label, hint, color = '#10B981' }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', padding: '8px 0' }}>
      <div
        onClick={() => onChange(!value)}
        style={{
          width: 44, height: 24, borderRadius: 12, cursor: 'pointer', flexShrink: 0,
          background: value ? color : 'var(--line)',
          position: 'relative', transition: 'background .2s',
        }}
      >
        <div style={{
          position: 'absolute', top: 3, left: value ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
        }} />
      </div>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{label}</div>
        {hint && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{hint}</div>}
      </div>
    </label>
  );
}
